import React, { useMemo } from "react";
import getShareImage from "@jlengstorf/get-share-image";
import { GatsbySeo } from "gatsby-plugin-next-seo";
import { encode } from "base-64";
import { truncate } from "lodash";
import { useTheme } from "@chakra-ui/react";

type PageSeoProps = {
  title: string;
  noIndex?: boolean;
  noFollow?: boolean;
  description?: string;
  socialTitle?: string;
  tagline?: string;
  thumb?: Queries.Course["thumb"];
};

export const PageSeo = ({
  title,
  description,
  noIndex = false,
  noFollow = false,
  socialTitle = title,
  tagline = truncate(description, { length: 100, separator: /,? +/ }),
  thumb,
}: PageSeoProps) => {
  const { colors } = useTheme();
  const socialCard = useMemo(() => {
    const titleFont = "odyssey:fonts:AeonikBold.otf";
    const taglineFont = "odyssey:fonts:InterRegular.ttf";
    const titleColor = colors.gray[800].slice(1);

    const truncatedTagline = truncate(tagline, {
      length: 100,
      separator: /,? +/,
    });

    const truncatedTitle = truncate(socialTitle, {
      length: 50,
      separator: /,? +/,
    });

    // the default overrides were taken from the social card config for devhub
    // https://github.com/apollographql/devhub/blob/master/src/components/Seo.js#L16-L22
    const options = {
      title: truncatedTitle,
      tagline: truncatedTagline,
      cloudName: "apollographql",
      titleExtraConfig: "_bold",
      titleFont,
      titleFontSize: 80,
      taglineFont,
      taglineFontSize: 36,
      titleColor,
      taglineColor: titleColor,
    };

    if (!thumb?.src) {
      return getShareImage({
        ...options,
        textLeftOffset: 80,
        imagePublicID: "odyssey_template_base_no_illustration_hfxjy9",
      });
    }

    const thumbOverlay = [
      `l_fetch:${encode(thumb.src)}`,
      "w_400",
      "h_669",
      "g_south_west", // which corner to use as the 'origin' point
      "c_fill", // crop like background-size: cover,
      "e_colorize",
      "co_rgb:C74100", // use a darker orange in our brand palette
    ].toString();

    const shareImage = getShareImage({
      ...options,
      imagePublicID: "odyssey_template_base_w_illustration_b4ovtn",
    });

    const parts = shareImage.split("/");
    return (
      parts
        .slice(0, -1)
        // splice thumb overlay into the second last URL segment
        .concat(thumbOverlay, parts[parts.length - 1])
        .join("/")
    );
  }, [colors.gray, thumb, socialTitle, tagline]);

  return (
    <GatsbySeo
      title={title}
      description={description}
      noindex={noIndex}
      nofollow={noFollow}
      openGraph={{
        title: socialTitle,
        description: tagline,
        images: [{ url: socialCard }],
      }}
      twitter={{
        cardType: "summary_large_image",
      }}
    />
  );
};
